/**
 * The three record kinds and their human-readable numbers.
 *
 *   INC000184   an incident: an interruption, degradation, fault or risk
 *   CHG000057   a change: an intentional production change
 *   RITM000093  a request item: an operational action that is not an incident
 *
 * Rows still carry UUIDs; the number is the OPERATIONAL reference, the thing
 * a person says out loud, types into search, and writes in a Slack thread.
 * Numbers are minted by a Postgres sequence per kind (migration 0059), so
 * they are globally unique and gap-tolerant: a rolled-back insert burns a
 * number and nothing cares. Six digits is the display minimum, not a cap -
 * `lpad` never truncates, so INC1000000 is simply the next number after
 * INC999999.
 */

export const RECORD_KINDS = ['INC', 'CHG', 'RITM'] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

export const RECORD_NUMBER_WIDTH = 6;

/** URL segment per kind, shared by the API paths and the operator UI routes. */
export const RECORD_KIND_PATH = { INC: 'incidents', CHG: 'changes', RITM: 'requests' } as const satisfies Record<RecordKind, string>;
export type RecordKindPath = (typeof RECORD_KIND_PATH)[RecordKind];

export const RECORD_KIND_BY_PATH: Readonly<Record<RecordKindPath, RecordKind>> = {
  incidents: 'INC', changes: 'CHG', requests: 'RITM',
};

export const RECORD_KIND_TITLE: Readonly<Record<RecordKind, string>> = {
  INC: 'Incident', CHG: 'Change', RITM: 'Request',
};

export function isRecordKind(v: unknown): v is RecordKind {
  return typeof v === 'string' && (RECORD_KINDS as readonly string[]).includes(v);
}

/** `formatRecordNumber('INC', 184)` -> `INC000184`. */
export function formatRecordNumber(kind: RecordKind, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1) throw new RangeError(`record sequence must be a positive integer, got ${seq}`);
  return `${kind}${String(seq).padStart(RECORD_NUMBER_WIDTH, '0')}`;
}

const NUMBER_RE = /^(INC|CHG|RITM)(\d{6,})$/;

/** The inverse of `formatRecordNumber`; null for anything that is not a record number. */
export function parseRecordNumber(s: string): { kind: RecordKind; seq: number } | null {
  const m = NUMBER_RE.exec(s.trim().toUpperCase());
  if (!m) return null;
  const seq = Number(m[2]);
  if (!Number.isSafeInteger(seq) || seq < 1) return null;
  return { kind: m[1] as RecordKind, seq };
}
